import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Collapse } from 'reactstrap';

import Icon, { faChevronDown, faChevronUp, IconProp } from '~components/icon';

interface ComponentProps {
  id: string;
  title: string;
  count: number;
  icon: IconProp;
  iconClassName?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

/**
 * ImportResultSection (TAV-3168)
 *
 * A collapsible result section with its count in the title, matching the cards on the
 * reconciliations process step and the statements confirm step (`WarningDetailsCard` /
 * `ErrorDetailsCard` in `~support-at-home/components/claims/common`).
 *
 * Those two are not reused directly because both are hard typed to the statements row shapes
 * (`WarningItem` / `ErrorItem`), which the budget import rows do not satisfy. Remapping budgets into
 * fake warnings to borrow the component would lose the real columns, so this keeps their markup and
 * takes arbitrary children instead.
 *
 * The header is a real `button` rather than the `CardHeader type="button"` those two use.
 * `CardHeader` renders a `div` by default, so their `type` attribute does nothing: the header is not
 * focusable, has no button role, and cannot be operated by keyboard. `tag="button"` gets focus,
 * Enter and Space from the platform for free. Pattern copied from `~dex/components/DexPreviewPanel`.
 */
const ImportResultSection: React.FC<ComponentProps> = ({
  id,
  title,
  count,
  icon,
  iconClassName,
  defaultOpen = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Children mount on the FIRST open and stay mounted from then on. A single import can carry
  // thousands of rows across the three sections, and TableView renders every row it is handed (it
  // has no client-side slicing - its pagination is server-driven via allowPagination / totalCount /
  // onLoadMore, none of which this modal passes). Mounting all three bodies eagerly therefore
  // renders the entire import into the DOM before the user has expanded anything.
  const [hasOpened, setHasOpened] = useState(defaultOpen);
  const bodyId = `${id}-body`;

  const toggle = () => {
    if (!isOpen) setHasOpened(true);
    setIsOpen(!isOpen);
  };

  return (
    <Card className="rounded-0 mb-2" data-cy={`${id}-card`}>
      <CardHeader
        className="d-flex justify-content-between border-0 fw-bold align-items-center"
        tag="button"
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        data-cy={`${id}-toggle`}
      >
        <div>
          <Icon icon={icon} className={iconClassName ? `${iconClassName} me-2` : 'me-2'} />
          {title} ({count})
        </div>
        <Icon icon={isOpen ? faChevronUp : faChevronDown} className="ms-2" />
      </CardHeader>
      {/*
        Collapse keeps the BODY mounted and height-animates it, so the aria-controls target above
        always resolves. A conditionally mounted body would leave that IDREF dangling while closed.
        Only the children inside it wait for the first open, which keeps that accessibility contract
        and the deferred render cost independent of each other.
      */}
      <Collapse isOpen={isOpen}>
        <CardBody className="p-0" id={bodyId}>
          {hasOpened ? children : null}
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default ImportResultSection;
